import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { filter } from 'rxjs/operators';
import { AuthService } from '@app/core/services/auth/auth.service';

interface MenuItem {
  label: string;
  icon: string;
  description?: string;
  routerLink?: string;
  items?: MenuItem[];
  badge?: string;
  badgeClass?: string;
  visible?: boolean;
  disabled?: boolean;
}

const ADMIN_ONLY_REPORT_LINKS = [
  '/app/reports/debtor-audit-trail',
  '/app/reports/debtors-aged-trial-balance',
];

@Component({
  selector: 'app-sidebar',
  templateUrl: './app-sidebar.component.html',
  styleUrls: ['./app-sidebar.component.scss'],
  imports: [
    CommonModule,
    InputTextModule,
    BadgeModule,
    ButtonModule,
    RippleModule,
    StyleClassModule,
  ],
})
export class AppSidebarComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);

  activeTab = 0;
  menuItems: MenuItem[] = [];
  userMenuOpen = false;
  expandedMenus = new Set<string>();
  constructor() {
    this.menuItems = [
      {
        label: 'Home',
        icon: 'pi pi-home',
        routerLink: '/app/home',
        description: 'Your workshop home',
      },
      {
        label: 'Customer',
        icon: 'pi pi-user',
        routerLink: '/app/customers',
        items: [
          {
            label: 'Customers',
            icon: 'pi pi-search',
            routerLink: '/app/customers',
            description: 'These are your customers',
          },
          {
            label: 'Debtors',
            icon: 'pi pi-wallet',
            routerLink: '/app/customers/debtors',
            description: 'Customers with outstanding balances or advance credit',
          },
          {
            label: 'Overdue Invoices',
            icon: 'pi pi-exclamation-circle',
            routerLink: '/app/customers/overdue-invoices',
            description: 'Overdue invoices you can bulk-email to customers',
          },
          {
            label: 'Notifications',
            icon: 'pi pi-bell',
            routerLink: '/app/notifications',
            description: 'Upcoming reminders and notification history',
          },
        ],
      },
      {
        label: 'Vehicle',
        icon: 'pi pi-car',
        routerLink: '/app/vehicles',
        items: [
          {
            label: 'Vehicles',
            icon: 'pi pi-search',
            routerLink: '/app/vehicles',
            description: 'These are your vehicles',
          },
          {
            label: 'Vehicle Makes',
            icon: 'pi pi-building',
            routerLink: '/app/vehicles/makes',
            description: 'Search for vehicle makes',
          },
          {
            label: 'Vehicle Models',
            icon: 'pi pi-tag',
            routerLink: '/app/vehicles/models',
            description: 'Search for vehicle models',
          },
          {
            label: 'Service Dues',
            icon: 'pi pi-clock',
            routerLink: '/app/vehicles/service-dues',
            description: 'Vehicles with WOF or service reminders due soon',
          },
        ],
      },
      {
        label: 'Job Templates (Kits)',
        icon: 'pi pi-box',
        routerLink: '/app/job-kits',
        items: [
          {
            label: 'Job Templates (Kits)',
            icon: 'pi pi-search',
            routerLink: '/app/job-kits',
            description: 'Manage your job kits',
          },
          {
            label: 'Labour Categories',
            icon: 'pi pi-clock',
            routerLink: '/app/job-kits/labour-categories',
            description: 'Manage labour categories',
          },
        ],
      },
      {
        label: 'Jobs & Quotes',
        icon: 'pi pi-wrench',
        routerLink: '/app/jobs',
        items: [
          {
            label: 'Jobs',
            icon: 'pi pi-calendar-plus',
            routerLink: '/app/jobs',
            description: 'This is your jobs',
          },
          {
            label: 'Quotes',
            icon: 'pi pi-file-edit',
            routerLink: '/app/quotes',
            description: 'Manage your quotes',
          },
          {
            label: 'Job Sources',
            icon: 'pi pi-bookmark',
            routerLink: '/app/jobs/sources',
            description: 'Manage job sources',
          },
          {
            label: 'Technicians',
            icon: 'pi pi-user',
            routerLink: '/app/jobs/technicians',
            description: 'Manage technicians',
          },
        ],
      },
      {
        label: 'Invoice',
        icon: 'pi pi-receipt',
        routerLink: '/app/invoices',
        items: [
          {
            label: 'Invoices',
            icon: 'pi pi-receipt',
            routerLink: '/app/invoices',
            description: 'This is your invoices',
          },
          {
            label: 'Banking',
            icon: 'pi pi-building-columns',
            routerLink: '/app/invoices/banking',
            description: 'Reconcile paid invoices against bank deposits',
            visible: true,
          },
        ],
      },
      {
        label: 'Reports',
        icon: 'pi pi-chart-bar',
        routerLink: '/app/reports',
        items: [
          {
            label: 'Dashboard',
            icon: 'pi pi-th-large',
            routerLink: '/app/reports/dashboard',
            description: 'Your workshop at a glance',
          },
          {
            label: 'Workshop Summary',
            icon: 'pi pi-chart-line',
            routerLink: '/app/reports/workshop-summary',
            description: 'Aggregate KPIs for jobs, invoices, and payments',
          },
          {
            label: 'Debtor Audit Trail',
            icon: 'pi pi-list',
            routerLink: '/app/reports/debtor-audit-trail',
            description: 'Chronological record of invoices raised and payments received',
          },
          {
            label: 'Debtors Aged Trial Balance',
            icon: 'pi pi-table',
            routerLink: '/app/reports/debtors-aged-trial-balance',
            description: 'Outstanding debtor balances aged by invoice due date',
          },
        ],
      },
    ];
  }

  async ngOnInit() {
    const role = await this.authService.getUserRole();
    this.buildMenuItems(role);

    // Set initial active tab based on current route
    this.setActiveTabFromRoute(this.router.url);

    // Subscribe to route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.setActiveTabFromRoute(event.url);
      });
  }

  private buildMenuItems(role: string | null): void {
    // Debtor reports need the ViewReport permission (tenant admin and above); hide
    // them from workshop managers, who would otherwise hit a 403 on open.
    if (role !== 'tenant_admin' && role !== 'system_admin') {
      const reports = this.menuItems.find((m) => m.routerLink === '/app/reports');
      if (reports?.items) {
        reports.items = reports.items.filter(
          (i) => !ADMIN_ONLY_REPORT_LINKS.includes(i.routerLink ?? '')
        );
      }
    }

    if (role === 'system_admin') {
      this.menuItems.push({
        label: 'Admin',
        icon: 'pi pi-shield',
        routerLink: '/app/admin',
        items: [
          {
            label: 'Tenants',
            icon: 'pi pi-building',
            routerLink: '/app/admin/tenants',
            description: 'Manage platform tenants',
          },
          {
            label: 'Trial requests',
            icon: 'pi pi-inbox',
            routerLink: '/app/admin/trial-requests',
            description: 'Approve or reject free trial requests',
          },
          {
            label: 'System Admins',
            icon: 'pi pi-user-plus',
            routerLink: '/app/admin/system-admins',
            description: 'Manage platform system administrators',
          },
        ],
      });
    }
  }

  toggleMenu(item: MenuItem): void {
    const key = item.label;
    if (this.expandedMenus.has(key)) {
      this.expandedMenus.clear();
    } else {
      this.expandedMenus.clear();
      this.expandedMenus.add(key);
    }
  }

  isMenuExpanded(item: MenuItem): boolean {
    return this.expandedMenus.has(item.label);
  }

  navigateTo(item: MenuItem) {
    if (item.routerLink) {
      this.router.navigate([item.routerLink]);
    }
  }

  async signOut() {
    await this.authService.signOut();
  }

  public toggleUserMenu() {
    this.userMenuOpen = !this.userMenuOpen;
  }

  private setActiveTabFromRoute(url: string) {
    const index = this.menuItems.findIndex((item) => {
      return item.routerLink && url.startsWith(item.routerLink);
    });

    if (index !== -1) {
      this.activeTab = index;
    }
  }
}
